/** Typed errors of the sync layer. None of them carries tokens, keys, passphrases or ciphertext. */

export class SyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = new.target.name;
  }
}

/** Error codes returned by the server (architecture.md section 6.2). */
export const ServerErrorCode = {
  Unauthorized: 'unauthorized',
  Forbidden: 'forbidden',
  NotFound: 'not_found',
  InvalidRequest: 'invalid_request',
  PayloadTooLarge: 'payload_too_large',
  QuotaExceeded: 'quota_exceeded',
  RateLimited: 'rate_limited',
  MethodNotAllowed: 'method_not_allowed',
  ServiceUnavailable: 'service_unavailable',
  InternalError: 'internal_error',
  /** Account endpoints (docs/architecture-account.md section 3.2). */
  EmailTaken: 'email_taken',
  SpaceTaken: 'space_taken',
  RegistrationClosed: 'registration_closed',
  PassphraseSpacesDisabled: 'passphrase_spaces_disabled',
  /** Push refused: the server requires a verified email and the account has none yet (403). */
  EmailUnverified: 'email_unverified',
  /** The response carried no recognisable error body. */
  Unknown: 'unknown',
} as const;
export type ServerErrorCode = (typeof ServerErrorCode)[keyof typeof ServerErrorCode];

/** The server answered with an error status. */
export class SyncHttpError extends SyncError {
  readonly status: number;
  readonly code: ServerErrorCode;
  /** Seconds the server asked the client to wait, when it sent `Retry-After`. */
  readonly retryAfter: number | null;
  constructor(status: number, code: ServerErrorCode, message: string, retryAfter: number | null = null) {
    super(`HTTP ${status} ${code}: ${message}`);
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

/** The request never produced a response: no connection, DNS failure, or the timeout elapsed. */
export class SyncNetworkError extends SyncError {
  readonly timedOut: boolean;
  constructor(message: string, timedOut: boolean) {
    super(message);
    this.timedOut = timedOut;
  }
}

/** The server's response did not match the protocol (wrong shape or non-advancing cursor). */
export class SyncProtocolError extends SyncError {}

export const DecryptFailure = {
  /** Not valid base64, or shorter than version byte, IV and authentication tag. */
  Malformed: 'malformed',
  /** The version byte is not one this client understands. */
  UnsupportedVersion: 'unsupportedVersion',
  /** AES-GCM authentication failed: wrong key, tampered ciphertext, or ciphertext of another record or space. */
  AuthenticationFailed: 'authenticationFailed',
  /** The plaintext is not a valid record envelope (corrupt, or written by a newer client). */
  InvalidEnvelope: 'invalidEnvelope',
  /** A valid envelope of a record type this build does not know (written by a newer client); the record is not corrupt. */
  UnknownRecordType: 'unknownRecordType',
  /** The envelope's identity or timestamp disagrees with the record metadata it arrived with. */
  MetadataMismatch: 'metadataMismatch',
} as const;
export type DecryptFailure = (typeof DecryptFailure)[keyof typeof DecryptFailure];

export class DecryptError extends SyncError {
  readonly reason: DecryptFailure;
  constructor(reason: DecryptFailure) {
    super(`Cannot decrypt record: ${reason}`);
    this.reason = reason;
  }
}

export class InvalidServerUrlError extends SyncError {
  constructor(message = 'The server address must be empty (same origin) or an http(s) URL') {
    super(message);
  }
}

/** An `http:` server address was given on a page served over `https:`; browsers block such requests. */
export class InsecureServerUrlError extends InvalidServerUrlError {
  constructor() {
    super('A page served over https cannot reach an http server (only this machine is exempt)');
  }
}

/** `joinSync` found no space for this passphrase: wrong passphrase, or the space was deleted. */
export class SpaceNotFoundError extends SyncError {
  constructor() {
    super('No synchronised data exists for this passphrase on the server');
  }
}

export class SyncAlreadyEnabledError extends SyncError {
  constructor() {
    super('Sync is already enabled on this device; disable it first');
  }
}

export class SyncNotEnabledError extends SyncError {
  constructor() {
    super('Sync is not enabled on this device');
  }
}

/** Sync is configured but the derived keys are missing from this browser profile. */
export class KeysMissingError extends SyncError {
  constructor() {
    super('The encryption keys of this device are missing; enter the passphrase again');
  }
}

/** Error code of a rejected login KDF parameter set (architecture-account.md section 3.2). */
export const KDF_PARAMS_REJECTED_CODE = 'kdf_params_rejected';

/** The server sent login KDF parameters outside the range the client accepts; nothing was derived. */
export class KdfParamsRejectedError extends SyncError {
  readonly code = KDF_PARAMS_REJECTED_CODE;
  constructor(message = 'The server sent unsafe key derivation parameters') {
    super(message);
  }
}

export const UnwrapFailure = {
  /** Not valid base64, or not exactly version byte, IV, 16-byte entropy and authentication tag. */
  Malformed: 'malformed',
  /** The version byte is not one this client understands. */
  UnsupportedVersion: 'unsupportedVersion',
  /** AES-GCM authentication failed: wrong login password, or a wrapped code of another space. */
  AuthenticationFailed: 'authenticationFailed',
} as const;
export type UnwrapFailure = (typeof UnwrapFailure)[keyof typeof UnwrapFailure];

/** The wrapped recovery code could not be opened. */
export class UnwrapFailedError extends SyncError {
  readonly reason: UnwrapFailure;
  constructor(reason: UnwrapFailure) {
    super(`Cannot unwrap the recovery code: ${reason}`);
    this.reason = reason;
  }
}

/** Registration found the email already in use (server with email verification off). */
export class EmailTakenError extends SyncError {
  constructor() {
    super('An account with this email already exists');
  }
}

/**
 * Registration (or upgrade) was accepted by the server but the account could not be logged in to with the
 * given password and space. A server that hides whether an email is registered answers the same way for an
 * email that already has an account, so the new account may not exist. Nothing was activated on this device.
 */
export class RegistrationNotConfirmedError extends SyncError {
  constructor() {
    super('The registration could not be confirmed by logging in');
  }
}

/** The user declined to continue because this device holds data of another space. Nothing was activated. */
export class ForeignLocalDataCancelledError extends SyncError {
  constructor() {
    super('Activation cancelled: this device holds data of another space');
  }
}

/** The space of the passphrase already belongs to another account. */
export class SpaceTakenError extends SyncError {
  constructor() {
    super('This sync space already belongs to an account');
  }
}

/** Wrong email or login password (the server does not say which, nor whether the email exists). */
export class InvalidCredentialsError extends SyncError {
  constructor() {
    super('The email or password is wrong');
  }
}

/** Wrong email or recovery code at password reset. */
export class InvalidRecoveryCodeError extends SyncError {
  constructor() {
    super('The email or recovery code is wrong');
  }
}

/** The server has no account endpoints (an older self-hosted server answers 404); the passphrase flow still works. */
export class AccountsUnsupportedError extends SyncError {
  constructor() {
    super('This server does not support accounts');
  }
}

/** The server does not accept new accounts (`WIZTIME_REGISTRATION=closed`). */
export class RegistrationClosedError extends SyncError {
  constructor() {
    super('This server does not accept registrations');
  }
}

/** The server refuses to create a space without an account (`WIZTIME_PASSPHRASE_SPACES` is `existing` or `off`). */
export class PassphraseSpacesDisabledError extends SyncError {
  constructor() {
    super('This server only offers sync to accounts');
  }
}

/** An account operation needs this device to be logged in to an account. */
export class NotAccountModeError extends SyncError {
  constructor() {
    super('This device is not logged in to an account');
  }
}

/** `upgradeToAccount` needs sync in passphrase mode; the device already uses an account. */
export class AlreadyAccountModeError extends SyncError {
  constructor() {
    super('This device is already logged in to an account');
  }
}

/** The passphrase entered for an upgrade does not belong to the space this device syncs. */
export class PassphraseMismatchError extends SyncError {
  constructor() {
    super('This passphrase does not belong to the space synchronised on this device');
  }
}

/** The recovery code the account returned derives a different space than the server announced; login is aborted. */
export class AccountSpaceMismatchError extends SyncError {
  constructor() {
    super('The account data does not match the space the server announced');
  }
}

/** The verification or email-change link is malformed, expired or already replaced (400 of `verify-email`). */
export class VerificationLinkInvalidError extends SyncError {
  constructor() {
    super('The verification link is invalid or has expired');
  }
}
